/**
 * agent 種類登錄（批141，裁示 13）——對應後端 `app/api/admin_agent_kinds.py`。
 *
 * 代管主機上的常駐 agent 不只一種：每一種在這裡登錄一列，決定「能不能註冊」
 * （未登錄的種類 enroll 一律 422）、「怎麼派工」（`v2_min_version` 起簽 IPOJOB1 schema 2、
 * `guest_min_version` 起送公開網址參數）與「怎麼安裝／升級」。
 *
 * 權限：讀＝admin 以上；寫＝營運主管以上。**沒有刪除**（主機以種類字串弱關聯這張表，
 * 刪一列＝那些主機再也註冊不進來）；`kind` 建立後不可改。代理商不開放（固定前綴
 * `/api/admin`，不走 `hosting.ts::base()`）。
 */
import { api } from './client'

export interface AgentKind {
  /** agent 自報的協定值（小寫英數與連字號，2–32 字）；建立後不可改。 */
  kind: string
  label: string
  /** 升級時去 dl-server 的哪個 channel 取包。 */
  dl_channel: string
  /** 安裝腳本在 dl-server 上的路徑（`/` 開頭）。 */
  installer_path: string
  /** `bash`｜`sh`（安裝指令的 `curl … | sudo <shell> -s -- …`）。 */
  installer_shell: string
  /** 心跳要求升到哪一版；null＝不催。 */
  target_version: string | null
  /**
   * 從哪一版起簽 IPOJOB1 schema 2（帶產品）。★ 批141-P 改語意：**null＝不啟用（一律簽
   * schema 1）**、`0.0.0`＝一律 schema 2。清空＝這個種類的主機退回舊簽章格式。
   */
  v2_min_version: string | null
  /** 從哪一版起送公開網址參數；null＝永遠不送。 */
  guest_min_version: string | null
  /**
   * 批151：從哪一版起支援**站台機密**（設定站台機密的派工、開站帶機密、agent 取值端點）。
   * null＝不支援（這個種類的主機不派站台機密）、`0.0.0`＝一律支援。
   */
  site_secrets_min_version: string | null
  /**
   * 批192：從哪一版起支援**預約更新**（維護時段、單次預約、依序更新佇列的新參數）。
   * null＝不支援（舊 agent 照舊行為）、`0.0.0`＝一律支援。
   */
  schedule_min_version: string | null
  notes: string | null
  /** 目前掛著這個種類的在役主機數（改門檻前的影響面提示）。 */
  hosts_count: number
  created_at: string | null
  updated_at: string | null
}

/** 可以改的欄位（部分更新：沒帶的欄位不動；版本四欄可明確送 null）。 */
export type AgentKindPatch = Partial<
  Pick<
    AgentKind,
    | 'label'
    | 'dl_channel'
    | 'installer_path'
    | 'installer_shell'
    | 'target_version'
    | 'v2_min_version'
    | 'guest_min_version'
    | 'site_secrets_min_version'
    | 'schedule_min_version'
    | 'notes'
  >
>

export type AgentKindIn = AgentKindPatch & {
  kind: string
  label: string
  dl_channel: string
  installer_path: string
}

/** 安裝指令能用的 shell（與後端 `INSTALLER_SHELLS` 同值）。 */
export const INSTALLER_SHELLS = ['bash', 'sh'] as const

export const listAgentKinds = () => api<{ items: AgentKind[] }>('/api/admin/agent-kinds')

/** 新增。409 `agent_kind_exists`；欄位錯 422 `{code, field, message}`。 */
export const createAgentKind = (body: AgentKindIn) =>
  api<AgentKind>('/api/admin/agent-kinds', { method: 'POST', body })

/** 部分更新。帶 `kind` 或未知欄位 → 422。 */
export const updateAgentKind = (kind: string, body: AgentKindPatch) =>
  api<AgentKind>(`/api/admin/agent-kinds/${encodeURIComponent(kind)}`, { method: 'PUT', body })
